import type { FC } from 'react'
import { memo, useCallback, useEffect, useState } from 'react'

import Cropper from 'react-easy-crop'
import { type Area } from 'react-easy-crop/types'
import { useDebounce } from 'use-debounce'

import { ImageCropperStyled } from './styles/ImageCropperStyled'
import { getCroppedImg } from './utils'
import { IconButton } from '../../elements/Button'
import { Flex } from '../../elements/Flex'
import { ButtonGroup } from '../ButtonGroup'
import { IconRotate, IconZoomIn, IconZoomOut } from '@ui/elements/Icon'

export interface IImageCropperProps {
  alt?: string
  image: string
  aspect?: number
  onChange?: (croppedImageUrl: string) => void
  minZoom?: number
  maxZoom?: number
  circleCrop?: boolean
  initialCroppedAreaPixels?: Area
  viewOnly?: boolean
  convertToBlob?: boolean
}

export const ImageCropper: FC<IImageCropperProps> = memo(
  ({
    alt = '',
    image,
    aspect,
    onChange,
    minZoom,
    maxZoom,
    circleCrop,
    initialCroppedAreaPixels,
    viewOnly,
    convertToBlob = false,
  }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [croppedAreaPixelsDebounced] = useDebounce(croppedAreaPixels, 300)

    const onCropComplete = useCallback((croppedArea, croppedAreaPxls) => {
      setCroppedAreaPixels(croppedAreaPxls)
    }, [])

    const onZoomInClick = () => {
      setZoom(Math.round((zoom + 0.5) * 10) / 10)
    }

    const onZoomOutClick = () => {
      setZoom(Math.round((zoom - 0.5) * 10) / 10)
    }

    const onRotateClick = () => {
      setRotation(rotation + 90)
    }

    useEffect(() => {
      setZoom(0.9)
      setRotation(0)
    }, [image])

    useEffect(() => {
      if (!croppedAreaPixels || viewOnly) return

      try {
        getCroppedImg(image, croppedAreaPixels, rotation, convertToBlob).then(
          (croppedImageUrl: string) => {
            onChange(croppedImageUrl)
          }
        )
      } catch (e) {
        console.error(e)
      }
    }, [croppedAreaPixelsDebounced])

    return (
      <ImageCropperStyled>
        <Cropper
          mediaProps={{
            alt,
          }}
          image={image}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          showGrid={false}
          minZoom={minZoom}
          maxZoom={maxZoom}
          style={{ cropAreaStyle: { width: '100%', height: '100%' } }}
          classes={{ cropAreaClassName: 'cropper-area' }}
          cropShape={circleCrop ? 'round' : 'rect'}
          initialCroppedAreaPixels={initialCroppedAreaPixels}
        />
        <Flex.Row justifyContent='center'>
          <ButtonGroup>
            <IconButton
              iconComponent={<IconZoomIn />}
              onClick={onZoomInClick}
              disabled={zoom >= maxZoom}
              aria-label='Zoom in button'
            />
            <IconButton
              iconComponent={<IconZoomOut />}
              onClick={onZoomOutClick}
              disabled={zoom <= minZoom}
              aria-label='Zoom out button'
            />
            <IconButton
              iconComponent={<IconRotate />}
              onClick={onRotateClick}
              aria-label='Rotate button'
            />
          </ButtonGroup>
        </Flex.Row>
      </ImageCropperStyled>
    )
  }
)

ImageCropper.defaultProps = {
  aspect: 1,
  onChange: () => undefined,
  minZoom: 1,
  maxZoom: 5,
  circleCrop: false,
  viewOnly: false,
}
ImageCropper.displayName = 'ImageCropper'
